import { useSelector } from "react-redux";
import { useEffect, useMemo, useRef } from "react";
import type { State } from "@App/state";
import { safeMap } from "@App/utils/array";
import type { GaragesRequest } from "@App/apis/graphql/perimeterSearch/requests/garagesRequest";
import { selectInitialRegistrationDate, selectNetworkId } from "@App/state/selectors/orders";
import { useStateDebounced } from "@App/hooks/useStateDebounced";

const deepEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);

export const useGaragesRequestFromStore = (
  variables: Partial<GaragesRequest> = {},
  debounceMs = 500,
): GaragesRequest | null => {
  const organization = useSelector((state: State) => state.orders.organization);
  const service = useSelector((state: State) => state.orders.service);

  const filterBrand = useSelector((state: State) => state.filters.brand);
  const brand = useSelector((state: State) => state.orders.brand);
  const generation = useSelector((state: State) => state.orders.generation);
  const motorization = useSelector((state: State) => state.orders.motorization);
  const body = useSelector((state: State) => state.orders.body);
  const gearbox = useSelector((state: State) => state.orders.gearbox);
  const mileage = useSelector((state: State) => state.orders.mileage);
  const initialRegistrationDate = useSelector(selectInitialRegistrationDate);
  const subRepairs = useSelector((state: State) => state.orders.subRepairs);
  const equipments = useSelector((state: State) => state.orders.equipments);
  const fleetVehicleReturn = useSelector((state: State) => state.fleet.vehicleReturn);
  const authorizedReturnLocationIds = fleetVehicleReturn?.authorizedReturnLocationIds;

  const place = useSelector((state: State) => state.orders.place);
  const search = useSelector((state: State) => state.filters.search);
  const premiumServices = useSelector((state: State) => state.orders.premiumServices);
  const dates = useSelector((state: State) => state.filters.dates);
  const distance = useSelector((state: State) => state.filters.distance);
  const sortBy = useSelector((state: State) => state.filters.sortBy);
  const bevSupport = useSelector((state: State) => state.filters.bevSupport);
  const onlyBookable = useSelector((state: State) => state.filters.onlyBookable);
  const onlyManufacturer = useSelector((state: State) => state.filters.onlyManufacturer);
  const commercialVehicleSupport = useSelector(
    (state: State) => state.filters.commercialVehicleSupport,
  );
  const presetFilters = useSelector((state: State) => state.filters.presetFilters);
  const garageProperties = useSelector((state: State) => state.filters.garageProperties);
  const garageTypeIds = useSelector((state: State) => state.filters.garageTypeIds);
  const driverId = useSelector((state: State) => state.auth?.driverData?.uuid);
  const networkId = useSelector(selectNetworkId);
  const garageChains = useSelector((state: State) => state.orders.garage_chain);

  // Derived request without side effects
  const draftRequest = useMemo<GaragesRequest | null>(() => {
    if (!organization?.id || !service?.id) return null;

    return {
      id: null,
      ids:
        authorizedReturnLocationIds && authorizedReturnLocationIds.length > 0
          ? authorizedReturnLocationIds
          : null,
      organizationId: organization.id,
      lat: place?.lat ?? null,
      lon: place?.lon ?? null,
      appointmentsFrom: dates?.from ?? null,
      appointmentsTo: dates?.to ?? null,
      distance,
      search,
      brandId: brand ? brand.id : filterBrand ? filterBrand.id : null,
      generationId: generation?.id ?? null,
      motorizationId: motorization?.id ?? null,
      bodyId: body?.id ?? null,
      gearboxId: gearbox?.id ?? null,
      mileage,
      registrationDate: initialRegistrationDate ?? null,
      services: service?.id ? [service.id] : [],
      sortBy,
      limit: 100,
      offset: 0,
      premiumServices: safeMap(premiumServices, (p) => p.id),
      equipments: safeMap(equipments, (e) => e.name),
      subRepairs,
      supportsBev: bevSupport,
      onlyBookable,
      driverId: driverId ?? null,
      supportsCommercialVehicles: commercialVehicleSupport,
      garageTypes: garageTypeIds?.length > 0 ? garageTypeIds : onlyManufacturer ? [2, 4] : null,
      garageChains: safeMap(garageChains, (gc) => gc.id),
      presetFilters,
      garageProperties,
      onlyPreferredPartners: null,
      networkId: networkId ?? null,
    };
  }, [
    organization,
    authorizedReturnLocationIds,
    service,
    place,
    dates,
    distance,
    search,
    brand,
    filterBrand,
    generation,
    motorization,
    body,
    gearbox,
    mileage,
    initialRegistrationDate,
    sortBy,
    premiumServices,
    equipments,
    subRepairs,
    bevSupport,
    onlyBookable,
    driverId,
    commercialVehicleSupport,
    onlyManufacturer,
    garageChains,
    presetFilters,
    garageProperties,
    garageTypeIds,
    networkId,
  ]);

  const [debouncedRequest, setDebouncedRequest] = useStateDebounced<GaragesRequest | null>(
    draftRequest,
    debounceMs,
  );

  // Only update debounced state if the *value* actually changed
  const lastDraftRef = useRef<GaragesRequest | null>(draftRequest);
  useEffect(() => {
    if (!deepEqual(draftRequest, lastDraftRef.current)) {
      lastDraftRef.current = draftRequest;
      setDebouncedRequest(draftRequest);
    }
  }, [draftRequest, setDebouncedRequest]);

  // Make the returned object stable. Also encourage callers to memoize `variables`.
  const variablesKey = useMemo(() => JSON.stringify(variables ?? {}), [variables]);
  return useMemo(
    () => (debouncedRequest ? { ...debouncedRequest, ...variables } : null),
    [debouncedRequest, variablesKey],
  );
};
